import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

const Buy3BHKVasundharaPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Why is 3BHK in Vasundhara better than 2BHK for long-term investment?",
      answer: "3BHK units in Vasundhara offer 50-70% more space (1,550-2,200 sq.ft vs 1,000-1,400 sq.ft) with superior appreciation potential (8-11% vs 6-9% for 2BHK). Premium societies like Mahagun Moderne show consistently higher capital growth. With upcoming metro connectivity, 3BHK units are positioned for significant value jumps."
    },
    {
      question: "What is the budget range for buying 3BHK in Vasundhara?",
      answer: "Budget ₹114 lakhs to ₹2.16 crores. Entry-premium options in Purvanchal Royal Park cost ₹114L-1.44Cr, mid-luxury units in Ajnara Daffodil are ₹1.32-₹1.68Cr, while ultra-premium properties in Mahagun Moderne and Saviour Park range from ₹1.68-₹2.16Cr. 99prop.com's zero brokerage saves you ₹114K-₹4.32L."
    },
    {
      question: "Which are the top 3BHK societies in Vasundhara?",
      answer: "Mahagun Moderne (₹1.68-₹2.16Cr, ultra-luxury), Saviour Park (₹1.5-₹1.86Cr, premium amenities), Ajnara Daffodil (₹1.32-₹1.68Cr, value luxury), and Supertech Ecovillage (₹1.2-₹1.56Cr, integrated township). All offer modern infrastructure with 8-11% appreciation potential."
    },
    {
      question: "How does buying through 99prop.com save money on 3BHK?",
      answer: "Traditional brokers charge 1-2% commission—that's ₹1.6-₹3.6L on a ₹1.8Cr property. 99prop.com's direct ownership model eliminates this entirely. Additionally, you get complimentary investment analysis (₹25K value), legal verification (₹20K), Vastu consultation (₹15K), and home loan coordination. Total savings: ₹2-4 lakhs."
    },
    {
      question: "What rental income can I expect from 3BHK in Vasundhara?",
      answer: "3BHK units command ₹30-55K monthly rent depending on society and furnishing. Ultra-premium furnished units in Mahagun Moderne fetch ₹50-55K, while unfurnished mid-range properties rent for ₹30-40K. This translates to 3.5-4.5% rental yields with strong demand from IT professionals and expat families."
    },
    {
      question: "Is now the right time to buy 3BHK in Vasundhara?",
      answer: "Yes. Vasundhara is in growth phase with upcoming metro connectivity (expected to boost values 20-25%). Current prices are 10-15% lower than mature areas like Indirapuram/Vaishali, offering entry advantage. 99prop.com's zero brokerage and direct pricing give you additional 1-2% value advantage. Buying now captures pre-metro appreciation."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "99Prop",
    "url": "https://99prop.com",
    "telephone": "+91-9891979097",
    "address": { "@type": "PostalAddress", "addressLocality": "Vasundhara", "addressRegion": "Ghaziabad", "addressCountry": "IN" },
    "priceRange": "₹85L - ₹1.6Cr",
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "520" }
  };

  return (
    <>
      <Helmet>
        <title>Buy 3BHK in Vasundhara | Ultra-Luxury Apartments | Zero Brokerage</title>
        <meta name="description" content="Buy spacious 3BHK flats in Vasundhara from ₹95L-₹1.8Cr. Zero brokerage, direct deals. Perfect for families & HNIs. Mahagun Moderne, Saviour Park properties." />
        <meta name="keywords" content="buy 3BHK Vasundhara, 3BHK flat Vasundhara, luxury apartments Vasundhara, Mahagun Moderne 3BHK, zero brokerage, upgrade to 3BHK" />
        <link rel="canonical" href="https://99prop.com/buy-3bhk-in-vasundhara" />
        <meta property="og:title" content="Buy 3BHK in Vasundhara | Ultra-Luxury Apartments | Zero Brokerage" />
        <meta property="og:description" content="Buy spacious 3BHK flats in Vasundhara from ₹95L-₹1.8Cr. Zero brokerage, perfect for upgrade buyers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://99prop.com/buy-3bhk-in-vasundhara" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-brand-500 to-brand-700 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">Buy 3BHK in Vasundhara</h1>
            <p className="text-xl sm:text-2xl text-brand-100 mb-8 max-w-3xl mx-auto">
              Ultra-Luxury Living | Zero Brokerage | 1,550-2,200 sq.ft | ₹114L-₹2.16Cr
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/buy-property')} className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl">
                View 3BHK Properties
              </button>
              <a href="tel:+919891979097" className="bg-brand-800 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-900 transition shadow-xl">
                Call: +91 9891 97 90 97
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <article className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-8">
            
            <section>
              <p className="text-lg text-gray-700 leading-relaxed">
                Ready to <strong>buy 3BHK in Vasundhara</strong> and experience ultra-luxury living? 99prop.com offers premium 3BHK properties (1,550-2,200 sq.ft) perfect for discerning families, upgrade buyers, and HNI investors. As a direct buying platform—not a listing portal—we eliminate the ₹1.5-3 lakh brokerage fee while providing exclusive access to Vasundhara's finest societies like Mahagun Moderne, Saviour Park, and Ajnara Daffodil. With upcoming metro connectivity and modern infrastructure, buying 3BHK in Vasundhara today positions you for exceptional appreciation while enjoying world-class amenities.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">3BHK Price Trends in Vasundhara (2026)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vasundhara's 3BHK segment shows strong momentum, driven by modern construction, upcoming metro, and premium buyer interest:
              </p>
              <div className="bg-brand-50 p-6 rounded-xl space-y-4">
                <div className="border-l-4 border-brand-600 pl-4">
                  <h3 className="text-lg font-bold text-gray-900">Premium Entry: ₹90-114 Lakhs</h3>
                  <p className="text-gray-700">Purvanchal Royal Park, older Supertech projects. 1,500-1,700 sq.ft. Good for first 3BHK buyers.</p>
                </div>
                <div className="border-l-4 border-brand-600 pl-4">
                  <h3 className="text-lg font-bold text-gray-900">Mid-Luxury: ₹1.32-₹1.68 Crores</h3>
                  <p className="text-gray-700">Ajnara Daffodil, Supertech Ecovillage. 1,650-1,900 sq.ft. Excellent balance of luxury and value.</p>
                </div>
                <div className="border-l-4 border-brand-600 pl-4">
                  <h3 className="text-lg font-bold text-gray-900">Ultra-Luxury: ₹1.68-₹2.16 Crores</h3>
                  <p className="text-gray-700">Mahagun Moderne, Saviour Park. 1,850-2,200 sq.ft. Premium amenities, 9-11% appreciation potential.</p>
                </div>
              </div>
              <p className="text-gray-700 mt-4">
                <strong>Growth Catalyst:</strong> Planned metro connectivity expected to boost 3BHK values by 20-25% within 2-3 years of announcement. Early buyers capture maximum upside.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Best Societies for 3BHK in Vasundhara</h2>
              <div className="space-y-6">
                {[
                  { name: "Mahagun Moderne", price: "₹1.68-₹2.16Cr", size: "1,900-2,200 sq.ft", desc: "Ultra-luxury with imported fittings, premium gym, rooftop infinity pool. Highest appreciation in Vasundhara (9-11% annually). Perfect for HNI families seeking status and investment." },
                  { name: "Saviour Park", price: "₹1.5-₹1.86Cr", size: "1,800-2,050 sq.ft", desc: "Premium society with excellent maintenance, clubhouse, landscaped gardens. Strong community. Popular among senior executives. Appreciation: 8-10% annually." },
                  { name: "Ajnara Daffodil", price: "₹1.32-₹1.68Cr", size: "1,650-1,900 sq.ft", desc: "Value luxury option. Well-maintained, good connectivity. Popular among families upgrading from 2BHK. Strong rental demand (₹45-55K/month)." },
                  { name: "Supertech Ecovillage", price: "₹1.2-₹1.56Cr", size: "1,600-1,850 sq.ft", desc: "Integrated township with schools, parks, retail inside. Ideal for families seeking convenience. Good for investors targeting rental income." }
                ].map((society, idx) => (
                  <div key={idx} className="bg-gray-50 p-6 rounded-xl border-l-4 border-brand-500">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{idx + 1}. {society.name}</h3>
                    <p className="text-gray-700 mb-2"><strong>Price:</strong> {society.price} | <strong>Size:</strong> {society.size}</p>
                    <p className="text-gray-700">{society.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-gradient-to-r from-brand-500 to-brand-600 p-8 rounded-xl text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Explore Ultra-Luxury 3BHK Properties</h3>
              <p className="text-brand-100 mb-6">Zero brokerage. Save ₹1.5-3 lakhs on commission.</p>
              <button onClick={() => navigate('/buy-property')} className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl">
                View 3BHK Inventory
              </button>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Who Should Buy 3BHK in Vasundhara?</h2>
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">👨‍👩‍👧‍👦 Expanding Families</h3>
                  <p className="text-gray-700">Families with 2+ children or multigenerational living (parents + kids). Third bedroom provides privacy for elderly parents or separate study space for children. Vasundhara's modern infrastructure ideal for families.</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">💼 Corporate Executives & Professionals</h3>
                  <p className="text-gray-700">Senior management requiring dedicated home office for confidential work. Third bedroom as professional workspace with separate entrance. NH-24 access crucial for business travel to Delhi/Noida offices.</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">📈 Savvy Upgrade Buyers</h3>
                  <p className="text-gray-700">Current 2BHK owners in Vasundhara/nearby seeking lifestyle upgrade. Can afford ₹80L+ investment. Recognize pre-metro pricing advantage. May rent out old 2BHK for ₹16-25K to offset new EMI.</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">🏆 HNI Investors & Luxury Seekers</h3>
                  <p className="text-gray-700">Budget ₹1.2Cr+ seeking ultra-luxury living in Mahagun Moderne/Saviour Park. Value imported fittings, premium amenities, status. Target 9-11% appreciation + 4-5% rental yields for portfolio diversification.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">3BHK vs 2BHK: Complete Analysis</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-brand-500 text-white">
                    <tr>
                      <th className="border border-gray-300 px-4 py-3 text-left">Parameter</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">2BHK</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">3BHK</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    {[
                      ["Carpet Area", "1,000-1,400 sq.ft", "1,550-2,200 sq.ft"],
                      ["Price (Vasundhara)", "₹48-85L", "₹75L-₹1.5Cr"],
                      ["Appreciation", "6-9%", "8-11%"],
                      ["Monthly Rent", "₹16-26K", "₹30-55K"],
                      ["Rental Yield", "3.5-4%", "3.5-4.5%"],
                      ["Target Buyers", "Young families, investors", "Growing families, HNIs, upgrade buyers"],
                      ["Resale Time", "35-50 days", "45-70 days"],
                      ["Maintenance", "₹3,500-6,000/month", "₹6,000-₹10,000/month"],
                      ["Best For", "Entry buyers, rental income", "Premium living, long-term wealth creation"]
                    ].map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? "bg-gray-50" : ""}>
                        <td className="border border-gray-300 px-4 py-3 font-semibold">{row[0]}</td>
                        <td className="border border-gray-300 px-4 py-3">{row[1]}</td>
                        <td className="border border-gray-300 px-4 py-3">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-green-50 p-6 rounded-xl mt-6">
                <h4 className="text-lg font-bold text-gray-900 mb-2">💡 Investment Decision Rule</h4>
                <p className="text-gray-700">
                  Choose 3BHK if you have 4+ family members, need home office, or budget supports ₹75L+. Seeking 8-11% appreciation? 3BHK is superior. 
                  <a href="/buy-2bhk-in-vasundhara" className="text-brand-600 font-semibold hover:underline ml-1">
                    Still evaluating 2BHK? Compare here →
                  </a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Investment Potential: Why Vasundhara 3BHK Outperforms</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Exceptional Appreciation: 8-11% Annually</h3>
                  <p className="text-gray-700">Ultra-premium units in Mahagun Moderne show 10-11% yearly growth. A ₹1.2Cr property today could be worth ₹1.8-2Cr in 5 years—outperforming most investment options with lower risk.</p>
                </div>
                <div className="border-l-4 border-brand-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Rental Income: ₹30-55K Monthly</h3>
                  <p className="text-gray-700">Furnished 3BHK units attract IT expats, senior executives, joint families. Premium units command ₹50-55K rent (₹6-6.6L annually), translating to 4-4.5% yields on ₹1.3-1.5Cr investment. Lower vacancy risk than 2BHK.</p>
                </div>
                <div className="border-l-4 border-brand-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Pre-Metro Advantage</h3>
                  <p className="text-gray-700">Upcoming metro connectivity will trigger 20-25% value jump. Buying now at pre-announcement prices captures full upside. Historical data from Vaishali shows 30-35% appreciation post-metro launch.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Zero Brokerage: Save ₹1.5-3 Lakhs</h2>
              <div className="space-y-6">
                {[
                  { num: 1, title: "We Own the Inventory", desc: "Direct ownership of select 3BHK units or exclusive builder tie-ups. Zero intermediaries = zero commission fees." },
                  { num: 2, title: "Transparent All-Inclusive Pricing", desc: "Our ₹1.2Cr price is final—not ₹1.17Cr + ₹3L surprise brokerage. Budget accurately." },
                  { num: 3, title: "Premium Services Complimentary", desc: "Investment ROI analysis (₹25K), legal title verification (₹20K), Vastu consultation (₹15K), home loan coordination—all free. Brokers charge extra." },
                  { num: 4, title: "Express 10-15 Day Closures", desc: "Direct buying eliminates multi-party negotiations. Documentation to possession in under 2 weeks for ready properties." }
                ].map(item => (
                  <div key={item.num} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-600 text-white rounded-full flex items-center justify-center font-bold text-xl">{item.num}</div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-700">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-yellow-50 p-6 rounded-xl mt-6">
                <h4 className="text-lg font-bold text-gray-900 mb-2">💰 Real Savings Example (2026)</h4>
                <p className="text-gray-700">
                  <strong>Property:</strong> 3BHK in Mahagun Moderne, 1,900 sq.ft<br/>
                  <strong>Via Broker:</strong> ₹1.85Cr property + ₹3.7L brokerage (2%) + ₹50K services = ₹1.92Cr<br/>
                  <strong>Via 99prop.com:</strong> ₹1.85Cr direct deal, all services free<br/>
                  <strong>Your Savings:</strong> ₹4.2 lakhs—fund luxury interiors or modular kitchen
                </p>
              </div>
            </section>

            <section className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Related Resources</h3>
              <ul className="space-y-2">
                <li><a href="/buy-property" className="text-brand-600 font-semibold hover:underline">→ Browse All 3BHK Properties</a></li>
                <li><a href="/buy-2bhk-in-vasundhara" className="text-brand-600 font-semibold hover:underline">→ Compare with 2BHK Options</a></li>
                <li><a href="/?start=true" className="text-brand-600 font-semibold hover:underline">→ Free Property Valuation</a></li>
                <li><a href="/buyer-strategy" className="text-brand-600 font-semibold hover:underline">→ Personalized Investment Strategy</a></li>
              </ul>
            </section>

            <section className="bg-gradient-to-r from-green-500 to-green-600 p-8 rounded-xl text-center">
              <h3 className="text-2xl font-bold text-white mb-3">WhatsApp for Instant Quote</h3>
              <a href="https://wa.me/919891979097?text=Hi, interested in 3BHK Vasundhara" target="_blank" rel="noopener noreferrer" className="bg-white text-green-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl inline-flex items-center gap-2">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp Now
              </a>
            </section>

            <section>
              <div className="grid md:grid-cols-4 gap-6 mb-6">
                <div className="bg-gradient-to-br from-brand-50 to-blue-50 p-6 rounded-xl text-center"><div className="text-4xl font-bold text-brand-600">520+</div><div className="text-gray-700 font-semibold">Sold</div></div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl text-center"><div className="text-4xl font-bold text-green-600">₹280Cr+</div><div className="text-gray-700 font-semibold">Value</div></div>
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl text-center"><div className="text-4xl font-bold text-orange-600">4.8/5</div><div className="text-gray-700 font-semibold">Rating</div></div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl text-center"><div className="text-4xl font-bold text-purple-600">7-15</div><div className="text-gray-700 font-semibold">Days</div></div>
              </div>
              <div className="space-y-4">
                <div className="bg-white p-5 rounded-xl shadow-md border-l-4 border-brand-500"><p className="text-gray-700"><strong>❓ Market rate?</strong> Yes, AI-verified pricing.</p></div>
                <div className="bg-white p-5 rounded-xl shadow-md border-l-4 border-green-500"><p className="text-gray-700"><strong>❓ Why trust?</strong> 520+ deals completed.</p></div>
                <div className="bg-white p-5 rounded-xl shadow-md border-l-4 border-blue-500"><p className="text-gray-700"><strong>❓ Negotiate?</strong> Limited 2-3%.</p></div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-gray-200 pb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-brand-600 p-8 rounded-xl text-center">
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Buy Your 3BHK in Vasundhara?</h2>
              <p className="text-brand-100 text-lg mb-6">Zero brokerage. Ultra-luxury properties. Expert advisory.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => navigate('/buy-property')} className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl">View 3BHK Inventory</button>
                <a href="tel:+919891979097" className="bg-brand-800 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-900 transition shadow-xl">Call: +91 9891 97 90 97</a>
              </div>
            </section>

          </article>
        </div>
      </div>
    </>
  );
};

export default Buy3BHKVasundharaPage;
